// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  FiLoader as Loader2, FiPlus as Plus, FiTrash2 as Trash2, FiEdit2 as Edit2,
  FiArrowRight as ArrowRight, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp,
  FiLayers as Layers
} from 'react-icons/fi';
import { getEnvironments, createEnvironment, updateEnvironment, deleteEnvironment, reorderEnvironments } from '../../../../../services/aiOperationsApi';

export const ENV_COLOR_OPTIONS = [
  { hex: '#3B82F6', label: 'Blue' },
  { hex: '#F59E0B', label: 'Yellow' },
  { hex: '#10B981', label: 'Green' },
  { hex: '#EF4444', label: 'Red' },
  { hex: '#8B5CF6', label: 'Purple' },
  { hex: '#EC4899', label: 'Pink' },
  { hex: '#06B6D4', label: 'Cyan' },
  { hex: '#F97316', label: 'Orange' },
];

const EnvironmentsContent = () => {
  const [envs, setEnvs] = useState([]);
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEnv, setEditingEnv] = useState(null);
  const [formData, setFormData] = useState({ name: '', display_name: '', description: '', color: '#3B82F6', is_terminal: false, position: 0 });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getEnvironments();
      setEnvs(data.environments || []);
      setChain(data.promotion_chain || []);
    } catch (err) {
      console.error('Error loading environments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreateOpen = () => {
    const maxPos = envs.length > 0 ? Math.max(...envs.map(e => e.position)) + 10 : 0;
    setFormData({ name: '', display_name: '', description: '', color: '#3B82F6', is_terminal: false, position: maxPos });
    setFormError('');
    setEditingEnv(null);
    setShowCreateForm(true);
  };

  const handleEditOpen = (env) => {
    setFormData({
      name: env.name,
      display_name: env.display_name,
      description: env.description || '',
      color: env.color || '#3B82F6',
      is_terminal: env.is_terminal,
      position: env.position,
    });
    setFormError('');
    setEditingEnv(env);
    setShowCreateForm(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.display_name) {
      setFormError('Name and display name are required.');
      return;
    }
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,49}$/.test(formData.name)) {
      setFormError('Name must be alphanumeric with hyphens/underscores, 1-50 chars.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      if (editingEnv) {
        await updateEnvironment(editingEnv.id, {
          display_name: formData.display_name,
          description: formData.description || null,
          color: formData.color,
          is_terminal: formData.is_terminal,
        });
      } else {
        await createEnvironment(formData);
      }
      setShowCreateForm(false);
      setEditingEnv(null);
      await loadData();
    } catch (err) {
      setFormError(err?.message || 'Failed to save environment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (env) => {
    if (!globalThis.confirm(`Delete environment "${env.display_name}"? This cannot be undone.`)) return;
    try {
      await deleteEnvironment(env.id);
      await loadData();
    } catch (err) {
      alert(err?.message || 'Failed to delete environment');
    }
  };

  const handleMoveUp = async (index) => {
    if (index <= 0) return;
    const newEnvs = [...envs];
    [newEnvs[index - 1], newEnvs[index]] = [newEnvs[index], newEnvs[index - 1]];
    const order = newEnvs.map((e, i) => ({ id: e.id, position: i * 10 }));
    try {
      const data = await reorderEnvironments(order);
      setEnvs(data.environments || []);
      setChain(data.promotion_chain || []);
    } catch (err) {
      alert(err?.message || 'Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    if (index >= envs.length - 1) return;
    const newEnvs = [...envs];
    [newEnvs[index], newEnvs[index + 1]] = [newEnvs[index + 1], newEnvs[index]];
    const order = newEnvs.map((e, i) => ({ id: e.id, position: i * 10 }));
    try {
      const data = await reorderEnvironments(order);
      setEnvs(data.environments || []);
      setChain(data.promotion_chain || []);
    } catch (err) {
      alert(err?.message || 'Failed to reorder');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  let saveButtonLabel: string;
  if (saving) saveButtonLabel = 'Saving...';
  else if (editingEnv) saveButtonLabel = 'Update';
  else saveButtonLabel = 'Create';

  return (
    <div>
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-6 mb-6 border border-indigo-100 dark:border-indigo-800">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-600" />
              Environments & Promotion Chain
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
              Define your deployment environments and their promotion order. Prompts move through this chain when promoted.
              Use the arrows to reorder the promotion chain.
            </p>
          </div>
          <button onClick={handleCreateOpen} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 whitespace-nowrap shadow-sm">
            <Plus className="w-5 h-5" /> Add Environment
          </button>
        </div>

        {chain.length > 0 && (
          <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">Promotion Chain</p>
            <div className="flex flex-wrap items-center gap-2">
              {envs.map((env, i) => (
                <React.Fragment key={env.id}>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: env.color || '#6B7280' }} />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{env.display_name}</span>
                    {env.is_terminal && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">terminal</span>
                    )}
                  </div>
                  {i < envs.length - 1 && <ArrowRight className="w-4 h-4 text-gray-400" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {envs.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Layers className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Environments Yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Add your first environment to start building a promotion chain.
          </p>
          <button onClick={handleCreateOpen} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            <Plus className="w-5 h-5" /> Add Environment
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {envs.map((env, index) => (
            <div key={env.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === envs.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: env.color || '#6B7280' }} />

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">{env.display_name}</span>
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{env.name}</span>
                      {env.is_terminal && (
                        <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded font-medium">Terminal</span>
                      )}
                    </div>
                    {env.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{env.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500 mr-2">Position: {env.position}</span>
                  <button
                    onClick={() => handleEditOpen(env)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(env)}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingEnv ? 'Edit Environment' : 'Add Environment'}
            </h3>

            <div className="space-y-4">
              {!editingEnv && (
                <div>
                  <label htmlFor="label-name-internal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name (internal)</label>
                  <input
                    id="label-name-internal"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value.toLowerCase().replaceAll(/[^a-z0-9_-]/g, '') }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                    placeholder="qa-testing"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Lowercase, alphanumeric, hyphens, underscores only.</p>
                </div>
              )}

              <div>
                <label htmlFor="label-display-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name</label>
                <input
                  id="label-display-name"
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData(f => ({ ...f, display_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="QA Testing"
                />
              </div>

              <div>
                <label htmlFor="label-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
                <input
                  id="label-description"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="Internal QA environment"
                />
              </div>

              <div>
                <label htmlFor="label-color" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {ENV_COLOR_OPTIONS.map(opt => (
                    <button
                      key={opt.hex}
                      onClick={() => setFormData(f => ({ ...f, color: opt.hex }))}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        formData.color === opt.hex ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: opt.hex }}
                      title={opt.label}
                    />
                  ))}
                </div>
              </div>

              <span className="flex items-center gap-2 cursor-pointer">
                <input
                  id="label-color"
                  type="checkbox"
                  checked={formData.is_terminal}
                  onChange={(e) => setFormData(f => ({ ...f, is_terminal: e.target.checked }))}
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Terminal environment (e.g., production)</span>
              </span>

              {formError && (
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowCreateForm(false); setEditingEnv(null); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saveButtonLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentsContent;
